import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  Box,
  useColorMode,
  ModalBody,
  IconButton,
  useDisclosure,
  FormLabel,
  Select,
  VStack,
  FormControl,
  Text,
  Input,
  ModalFooter,
  useToast,
  FormHelperText,
  Tooltip
} from "@chakra-ui/react";
import { default as ReactSelect } from "react-select";
import AsyncSelect from "react-select/async";
import { AddUserIcon, EditIcon } from "../../shared/icons";
import ModalSkeleton from "@/shared/components/ModalSkeleton";
import { PERMISSIONS_LIST } from "@/consts";
import theme from '../../shared/theme';
import { reactSelectDarkColors, secondaryBtnStyle} from "../../shared/theme";
import { StaticRow } from "@/components/elements";
import { TOAST_POSITION, STANDARD_ERROR_MSG } from "../../consts";
import { useTranslation } from "react-i18next";
import { startCase } from "lodash";
import {Button} from '@/shared/components/core/Button'
import { fetchCompanies, fetchCompanyCoordinates, upsertCompany } from "../../api/companies";
import {KeyIcon} from '@/shared/icons'
import { getToken } from '../../api/minds'
import { Chart, registerables } from 'chart.js';
import { Bar } from "react-chartjs-2";

export const AddUserModal = ({ users, onSubmit }) => {
  const { onOpen, isOpen, onClose } = useDisclosure();
  const { colorMode } = useColorMode();
  const [selectedUser, setSelectedUser] = useState();
  const [permission, setPermission] = useState(1);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const toast = useToast()
  const { t } = useTranslation(['general', 'minds'])

  useEffect(() => {
    if (selectedUser) {
      const permissions = PERMISSIONS_LIST.filter(
        (p) => selectedUser.level.id >= p.id
      );
      setAvailablePermissions(permissions);
    }
  }, [selectedUser]);

  return (
    <>
      <IconButton
        icon={<AddUserIcon />}
        size="sm"
        fontSize="lg"
        onClick={() => onOpen()}
        {...secondaryBtnStyle({ colorMode })}
      />
      <ModalSkeleton isOpen={isOpen} onClose={onClose} title={startCase(t('general:add-user', "add user"))}>
        <ModalBody>
          <Box
            as="form"
            w="full"
            id='add_user_to_mind'
            onSubmit={(e) => {
              e.preventDefault();
              // TODO: Add api call
              onSubmit({ permission, selectedUser })
                .then(() => onClose())
                .catch(reason => {
                  toast({
                    status: 'error',
                    description: STANDARD_ERROR_MSG,
                    position: TOAST_POSITION,
                    isClosable: true,
                  })
                  console.error(reason)
                })
            }}
          >
            <VStack align="flex-start">
              <FormControl w="full" id="users">
                <FormLabel>User</FormLabel>
                <ReactSelect
                  theme={colorMode === 'dark' && { colors: reactSelectDarkColors }}
                  name="users"
                  isSearchable={true}
                  isClearable={true}
                  onChange={(s) => {
                    s ? setSelectedUser(s.user) : setSelectedUser();
                  }}
                  options={users.map((u) => ({
                    value: u.id,
                    label: u.fullname || u.email,
                    user: u,
                  }))}
                />
              </FormControl>

              <FormControl isDisabled={!selectedUser}>
                <FormLabel>{t('general:permission', 'permission').toCapitalCase()}</FormLabel>
                <Select
                  value={permission}
                  onChange={(e) => setPermission(e.target.value)}
                >
                  {availablePermissions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

            </VStack>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} variant='secondary' mr={2}>
            {t('general:Cancel', 'Cancel')}
          </Button>
          <Button
            alignSelf="flex-end" variant='primary' type="submit" form='add_user_to_mind'>
            {t('general:Submit', 'Submit')}
          </Button>
        </ModalFooter>
      </ModalSkeleton>
    </>
  );
};

const joinAddress = (...parts) => parts.filter(Boolean).join(', ');

const buildAddressOptions = (company) => {
  const main = {
    value: 'main',
    label: joinAddress(
      company.indirizzo, company.cap, company.localita, company.nazione
    ),
    coords: {
      cap: company.cap,
      localita: company.localita,
      country_code: company.country_code,
    },
    destination: {
      name: company.ragione_sociale || '',
      address: company.indirizzo || '',
      locality: company.localita || '',
      zip_code: company.cap || '',
      state_code: '',
      country: company.country_code || '',
      is_company_address: true,
    },
  };
  // Only the entries that identify a place, by the same rule the server
  // stores them (_DESTINATION_IDENTITY_FIELDS). A CRM entry with no name,
  // street, postcode or town cannot be stored: submitting it made the backend
  // delete the plant the Mind already had while the toast reported success.
  // Filtered after the mapping, so `value` keeps the index of the CRM list.
  const plants = (company.addresses_info || []).map((a, i) => ({
    value: i,
    label: joinAddress(a.name, a.indirizzo, a.cap, a.localita, a.nazione),
    coords: {
      cap: a.cap,
      localita: a.localita,
      country_code: a.country_code,
    },
    destination: {
      name: a.name || '',
      address: a.indirizzo || '',
      locality: a.localita || '',
      zip_code: a.cap || '',
      state_code: a.state_code || '',
      country: a.country_code || '',
      is_company_address: false,
    },
  })).filter(({ destination }) => Boolean(
    // Trimmed, because that is what the server compares: DRF's CharFields
    // strip whitespace, so an entry whose only field is ' ' identifies a place
    // here and nothing there — it would pass the filter and then delete the
    // stored plant, which is the very thing this filter is for.
    destination.name.trim() || destination.address.trim()
    || destination.zip_code.trim() || destination.locality.trim()
  ));
  return [main, ...plants];
};

// Whether a CRM address is the plant the Mind is already stored in. Matched on
// the identifying fields only: the country goes through normalize_country_code
// on its way into the row, so a territory it drops is stored blank while the
// CRM option still carries the raw code, and comparing it would never match.
const sameDestination = (option, stored) => Boolean(option) && Boolean(stored)
  && (option.name || '') === (stored.name || '')
  && (option.address || '') === (stored.address || '')
  && (option.zip_code || '') === (stored.zip_code || '')
  && (option.locality || '') === (stored.locality || '');

export const EditCompanyModal = ({ currentCompany, destination, onSubmit }) => {
  const [company, setCompany] = useState(currentCompany);

  const { name, latitude, longitude, country, sector } = company;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [companyToSubmit, setCompanyToSubmit] = useState();
  const [companiesCount, setCompaniesCount] = useState(-1)
  const [coordinate, setCoordinate] = useState();
  const [addressOptions, setAddressOptions] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  // Seeded with the installation site as the serializer resolved it, so the
  // address and coordinate rows open on the place the Mind is actually in, and
  // so submitting an untouched modal writes that same place back: the company
  // change clears the plant through Mind.save(). `null` means 'send no
  // destination', which is not the same as the company address: it leaves the
  // stored row alone instead of dropping it.
  const [destinationToSubmit, setDestinationToSubmit] = useState(destination);
  const [geocodingPlant, setGeocodingPlant] = useState(false);
  const [displayAddress, setDisplayAddress] = useState(
    destination.display_address
  );
  const { colorMode } = useColorMode();
  const toast = useToast()
  const { t } = useTranslation(['general', 'minds'])

  const promiseOptions = (inputValue) => fetchCompanies(inputValue)
    .then(resp => {
      const [options, data] = resp
      setCompaniesCount(data.count)
      return options
    });

  const plantSelection = useRef({ id: 0, coordsEdited: false })
  const invalidatePlantSelection = () => {
    plantSelection.current = { id: plantSelection.current.id + 1, coordsEdited: false }
    return plantSelection.current.id
  }

  const editingPlant = Boolean(
    destinationToSubmit && !destinationToSubmit.is_company_address
  )

  const shownCoordinate = (axis) => {
    const value = editingPlant ? destinationToSubmit[axis] : company[axis]
    return value === undefined || value === null ? '' : value
  }

  const changeCoordinate = (axis) => (e) => {
    const value = e.target.value
    if (editingPlant) {
      plantSelection.current.coordsEdited = true
      setGeocodingPlant(false)
      setDestinationToSubmit((prev) => ({ ...prev, [axis]: value }))
      return
    }
    setCompanyToSubmit({ ...companyToSubmit, [axis]: value })
    setCompany({ ...company, [axis]: value })
    setCoordinate(company[axis])
  }
  
  useEffect(() => {
    if (!isOpen) {
      // Reset
      setCompany(currentCompany)
      setCompanyToSubmit(undefined)
      setAddressOptions(null)
      setSelectedAddress(null)
      setDestinationToSubmit(destination)
      setGeocodingPlant(false)
      setDisplayAddress(destination.display_address)
      invalidatePlantSelection()
    }
  }, [isOpen, destination])


  useEffect(() => {
    axios.post('/api/get_country/', company)
    .then(({data}) =>{
      if (data.message){
        toast({
          status: 'error',
          description: data.message,
          position: TOAST_POSITION,
          isClosable: true,
        })
      }
      setCompany({
        name: data.name,
        address: data.address,
        latitude: data.latitude,
        longitude: data.longitude,
        country:{ code :data.country.code ,name : data.country.name},
        serial: data.serial,
        sector: data.sector
      });
    })
  }, [coordinate])

  return (
    <>
      <EditIcon onClick={onOpen} cursor="pointer" />
      <ModalSkeleton
        isOpen={isOpen}
        title={`${t('general:edit', 'edit').toCapitalCase()} MiND ${t('general:company', 'company').toCapitalCase()}`}
        onClose={onClose}
        size="xl"
      >
        <ModalBody>
          <VStack
            align="flex-start"
            w="full"
            as="form"
            id="companyForm"
            onSubmit={(e) => {
              e.preventDefault();
              let dataToSubmit = {
                ...company,
                ...companyToSubmit,
                country: country.code
              }
              dataToSubmit =  sector ? {...dataToSubmit, sector: sector.id} : {...dataToSubmit}

              upsertCompany(dataToSubmit)
                .then(data => {
                  onSubmit(data, destinationToSubmit)
                  .then(() => onClose())
                  .catch((reason) => {
                    toast({
                      status: 'error',
                      description: STANDARD_ERROR_MSG,
                      position: TOAST_POSITION,
                      isClosable: true,
                    })
                    console.error(reason)
                  })
                })
                .catch(reason => {
                  toast({
                    status: 'error',
                    description: STANDARD_ERROR_MSG,
                    position: TOAST_POSITION,
                    isClosable: true,
                  })
                  console.error(reason)
                })

            }}
          >
            <FormControl my={4} w="inherit" id='search_company'>
              {companiesCount > 20 && <FormHelperText mb={1}>
                  {t('minds:refine-company-search', 'If you do not find the searched company please refine the search...')}
                </FormHelperText>}
              <AsyncSelect
                placeholder={`${t('general:Search', 'Search')} ${t('general:company', 'company')}...`}
                theme={colorMode === 'dark' && {colors:  reactSelectDarkColors}}
                onChange={(c) => {
                  fetchCompanyCoordinates(c.company).then((coordinates) => {
                    const selectedCompnayBEnames = {
                      ...c.company,
                      ...coordinates,
                    };
                    const companyAddress = joinAddress(
                      selectedCompnayBEnames.indirizzo,
                      selectedCompnayBEnames.cap,
                      selectedCompnayBEnames.localita,
                      selectedCompnayBEnames.nazione,
                    );
                    setCompany({
                      name: selectedCompnayBEnames.ragione_sociale,
                      address: companyAddress,
                      latitude: selectedCompnayBEnames.latitude,
                      longitude: selectedCompnayBEnames.longitude,
                      country: { name: selectedCompnayBEnames.nazione },
                    });
                    setCompanyToSubmit(selectedCompnayBEnames);
                    const options = buildAddressOptions(c.company);
                    // Counted on the options, not on the CRM list: an entry
                    // that identifies no place is dropped, and a dropdown
                    // holding nothing but the company address is no choice.
                    const hasPlants = options.length > 1;
                    setAddressOptions(hasPlants ? options : null);
                    invalidatePlantSelection();
                    setGeocodingPlant(false);
                    // Pre-select the plant the Mind is already in, so its
                    // coordinates stay on screen and re-picking the same
                    // company is a no-op. Forcing the first option instead
                    // silently deleted a plant the user never touched, because
                    // it carries is_company_address.
                    const stored = !destination.is_company_address
                      && options.find((o) => (
                        !o.destination.is_company_address
                        && sameDestination(o.destination, destination)
                      ));
                    if (stored) {
                      setSelectedAddress(stored);
                      setDestinationToSubmit({
                        ...stored.destination,
                        latitude: destination.latitude,
                        longitude: destination.longitude,
                      });
                      setDisplayAddress(stored.label);
                    } else {
                      // Nothing to match: the company's own address is the
                      // default, and it is shown as such. Left empty, a company
                      // with several addresses opened on a placeholder that
                      // read as 'unset' when it is not.
                      // What is submitted does not follow the selection here:
                      // `null` means 'send no destination', which leaves a
                      // stored plant alone, while the company option carries
                      // is_company_address and would delete it — the plant the
                      // user never touched, for a company they only re-picked.
                      setSelectedAddress(
                        options.find((o) => o.destination.is_company_address)
                        || null
                      );
                      setDestinationToSubmit(null);
                      setDisplayAddress(companyAddress);
                    }
                  });
                }}
                cacheOptions
                noOptionsMessage={() => (
                  companiesCount === -1
                    ? t('minds:company-type-to-load-options', 'Type to load the available options')
                    : t('minds:no-options', 'no options').toCapitalCase()
                )}
                loadOptions={promiseOptions}
              />
            </FormControl>
            <Box px={4}>
              <StaticRow label={t('general:name', 'name').toCapitalCase()} labelProps={{w: '40%'}} content={<Text w='70%'>{name}</Text>} />
              <StaticRow
                label={t('general:address', "address").toCapitalCase()}
                labelProps={{w: '40%'}}
                content={
                  addressOptions ? (
                    <Box w='70%'>
                      <ReactSelect
                        theme={colorMode === 'dark' && { colors: reactSelectDarkColors }}
                        placeholder={t('minds:select-destination-address', 'Select destination address...')}
                        value={selectedAddress}
                        options={addressOptions}
                        onChange={(opt) => {
                          setSelectedAddress(opt);
                          setDisplayAddress(opt.label);
                          const selectionId = invalidatePlantSelection();

                          // The company's own address is not geocoded again:
                          // the rows fall back to the company coordinates,
                          // fetched when the company was picked.
                          if (opt.destination.is_company_address) {
                            setDestinationToSubmit(opt.destination);
                            setGeocodingPlant(false);
                            return;
                          }

                          // Back to the stored plant: keep the coordinates the
                          // user already approved instead of blanking them for
                          // a geocode that may answer something else, or (0, 0).
                          // Only a stored plant can be gone back to: the
                          // company site is always an object now, and matching
                          // a plant against it would lend it the company's
                          // coordinates and skip the geocode.
                          if (!destination.is_company_address
                              && sameDestination(opt.destination, destination)) {
                            setDestinationToSubmit({
                              ...opt.destination,
                              latitude: destination.latitude,
                              longitude: destination.longitude,
                            });
                            setGeocodingPlant(false);
                            return;
                          }

                          setDestinationToSubmit(opt.destination);
                          setGeocodingPlant(true);
                          fetchCompanyCoordinates(opt.coords).then(({ latitude, longitude }) => {
                            const selection = plantSelection.current;
                            // A newer selection owns the rows now, and it has
                            // already set the flag for its own lookup.
                            if (selection.id !== selectionId) {
                              return;
                            }
                            setGeocodingPlant(false);
                            if (selection.coordsEdited) {
                              return;
                            }
                            setDestinationToSubmit((prev) => ({ ...prev, latitude, longitude }));
                          });
                        }}
                      />
                    </Box>
                  ) : (
                    <Text w='70%'>{displayAddress}</Text>
                  )
                }
              />
              <StaticRow
                label={t('general:latitude', "latitude").toCapitalCase()}
                labelProps={{w: '40%'}}
                content={
                  <Input type='number' id='latitude' px={2} w='200px'
                    value={shownCoordinate('latitude')}
                    onChange={changeCoordinate('latitude')}
                    isRequired />
                }
              />
              <StaticRow
                label={t('general:longitude', "longitude").toCapitalCase()}
                labelProps={{w: '40%'}}
                content={
                  <Input type='number' id='longitude' px={2} w='200px'
                    value={shownCoordinate('longitude')}
                    onChange={changeCoordinate('longitude')}
                    isRequired />
                }
                />
                  <StaticRow label={t('general:country', "country").toCapitalCase()} labelProps={{ w: '40%' }}
                    content={country.name} />
                    {/* <StaticRow label='Sector' content={sector.name}/> */}
            </Box>
          </VStack>
        </ModalBody>

          <ModalFooter>
            <Button
              alignSelf="flex-end" variant='secondary'
              onClick={onClose}
            >
              {t('general:Cancel', 'Cancel')}
            </Button>
            <Button
              ml={3}
              form="companyForm"
              alignSelf="flex-end"
              variant='primary'
              type="submit"
              // The coordinate rows are empty until the lookup answers, and an
              // empty number input submits no coordinate at all, which the API
              // stores as 0, 0 — the Gulf of Guinea.
              isDisabled={geocodingPlant}
              isLoading={geocodingPlant}
            >
              {t('general:Submit', 'Submit')}
            </Button>
          </ModalFooter>
      </ModalSkeleton>
    </>
  );
};


export const AccessKey = ({mid}) => {
  const [token, setToken] = useState()
  const {t} = useTranslation(['general', 'minds'])
  const toast = useToast()

  const successToast = () => {
    toast({
      title: t('minds:token-copied', 'Access token copied to clipboard').toCapitalCase(),
      position: TOAST_POSITION,
      isClosable: true,
      })
    }

  return (
    <>
      <Tooltip placement='top' label={
          <Box>
            <Box>{t('general:access-token', 'access token').toCapitalCase()}</Box>
          </Box>
        }>
        <KeyIcon
          cursor='pointer'
          fontSize={{base: 'xs', md: 'md'}}
          onClick={() => {
          if (token) {
            navigator.clipboard.writeText(token)
            successToast()
          }
          else {
            getToken(mid)
            .then(({data: {token, error}}) => {
              if (token){
                setToken(token)
                navigator.clipboard.writeText(token)
                successToast()
              } else {
                toast({
                  status: 'error',
                  description: error,
                  position: TOAST_POSITION,
                  isClosable: true,
                })
              }
            })
          }
        }}/>
      </Tooltip>
    </>
  )
};

export const ConnectionHistory = ({datasets, onClick=null, date_type=null}) => {
  const { colorMode } = useColorMode()
  Chart.register(...registerables);
  const currentHour = new Date().getHours();
  let labels = Array.from({length: 24}, (_, i) => (i + currentHour + 1) % 24) 

  if (date_type === 'month') {
    labels = Array.from({length: 31}, (_, i) => (i + 1)) // Days of the month
  } else if (date_type === 'day') {
    labels = Array.from({length: 24}, (_, i) => (i)) // Hours of the day
  }
  const chartRef = useRef();
  return (
    <Bar
      onClick={event => {
        if (onClick && date_type === 'month' && event && chartRef.current) {
          const chart = chartRef.current;
          // Get the clicked element
          const points = chart.getElementsAtEventForMode(event.nativeEvent, 'nearest', { intersect: true }, true);
          if (points.length > 0) {
            const dayIndex = points[0].index;
            const selectedDay = labels[dayIndex];
            onClick(selectedDay);
          }
        }
      }}
      ref={chartRef}
      data={{
        labels: labels,
        datasets: datasets
      }}
      options={{
        plugins: {
          title: {
            display: false,
          },
          legend: {
            display: true,
            labels: {
              boxWidth: 12,
              useBorderRadius: true,
              borderRadius: 6,
              color: colorMode === 'light' ? theme.colors.frigel.neutral.darkGray : theme.colors.frigel.neutral.white
            },
          }
        },
        scales: {
          x: {
            stacked: true,
            grid: {
              color: colorMode === 'light' ? Chart.defaults.borderColor : 'white',
            },
            ticks: {
              color: colorMode === 'light' ? theme.colors.frigel.neutral.darkGray : theme.colors.frigel.neutral.white
            },
            border: {
              color: colorMode === 'light' ? Chart.defaults.borderColor : 'white',
            }
          },
          y: {
            stacked: true,
            grid: {
              display: false,
            },
            ticks: {
              display: false
            },
            border: {
              color: colorMode === 'light' ? Chart.defaults.borderColor : 'white',
            }
          }
        },
        responsive: true,
        maintainAspectRatio: false
      }}
    />
  )
}